import { createApi } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import baseQueryWithTokenManagement from '../../pages/auth/apiSlice';
import type { InboundPackage, OutboundPackage, PackageListArgs } from '../../models/Warehouse';

export const warehousePackagesApi = createApi({
  tagTypes: [
    'INBOUND_PACKAGES',
    'INBOUND_PACKAGE_DETAILS',
    'OUTBOUND_PACKAGES',
    'OUTBOUND_PACKAGE_DETAILS',
    'PACKAGE_ITEM_DETAILS',
  ],
  reducerPath: 'warehousePackagesApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    // Inbound packages endpoints
    inboundPackageList: builder.query<{ packages: InboundPackage[]; total: number }, PackageListArgs>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };

        return {
          url: `/wrd/api/purchase-order/warehouse/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content || [];
        const total = response?.data?.totalElements || 0;

        const packages = content.map((item: any) => ({
          ...item,
          formattedShipmentDate: item.shipmentDate
            ? {
                full: moment(item.shipmentDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.shipmentDate).format('DD.MM.YYYY'),
              }
            : undefined,
          formattedExpectedArrivalDate: item.expectedArrivalDate
            ? {
                full: moment(item.expectedArrivalDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.expectedArrivalDate).format('DD.MM.YYYY'),
              }
            : undefined,
        }));

        return { packages, total };
      },
      providesTags: () => ['INBOUND_PACKAGES'],
    }),

    // Inbound package details
    inboundPackageById: builder.query<InboundPackage, string>({
      query: (id) => ({
        url: `/wrd/api/purchase-order/warehouse/${id}/details`,
      }),
      transformResponse: (response: any) => {
        const item = response?.data || {};
        return {
          ...item,
          formattedShipmentDate: item.shipmentDate
            ? {
                full: moment(item.shipmentDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.shipmentDate).format('DD.MM.YYYY'),
              }
            : undefined,
          formattedExpectedArrivalDate: item.expectedArrivalDate
            ? {
                full: moment(item.expectedArrivalDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.expectedArrivalDate).format('DD.MM.YYYY'),
              }
            : undefined,
        };
      },
      providesTags: ['INBOUND_PACKAGE_DETAILS'],
    }),

    // Outbound packages endpoints
    outboundPackageList: builder.query<{ packages: OutboundPackage[]; total: number }, PackageListArgs>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };

        return {
          url: `/wrd/api/order/warehouse/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content || [];
        const total = response?.data?.totalElements || 0;

        const packages = content.map((item: any) => ({
          ...item,
          formattedExpectedDeliveryDate: item.expectedDeliveryDate
            ? {
                full: moment(item.expectedDeliveryDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.expectedDeliveryDate).format('DD.MM.YYYY'),
              }
            : undefined,
        }));

        return { packages, total };
      },
      providesTags: () => ['OUTBOUND_PACKAGES'],
    }),

    // Outbound package details
    outboundPackageById: builder.query<OutboundPackage, string>({
      query: (id) => ({
        url: `/wrd/api/order/warehouse/${id}/details`,
      }),
      transformResponse: (response: any) => {
        const item = response?.data || {};
        return {
          ...item,
          formattedExpectedDeliveryDate: item.expectedDeliveryDate
            ? {
                full: moment(item.expectedDeliveryDate).format('DD.MM.YYYY HH:mm:ss'),
                day: moment.utc(item.expectedDeliveryDate).format('DD.MM.YYYY'),
              }
            : undefined,
        };
      },
      providesTags: ['OUTBOUND_PACKAGE_DETAILS'],
    }),

    // Identify order from delivery note
    identifyOrder: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/wrd/api/purchase-order/warehouse/identify-order',
        method: 'post',
        body: formData,
      }),
      invalidatesTags: ['INBOUND_PACKAGES', 'INBOUND_PACKAGE_DETAILS'],
    }),

    // Identify item from image
    identifyItem: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/wrd/api/purchase-order/warehouse/identify-item',
        method: 'post',
        body: formData,
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS'],
    }),

    // Update item status
    updateItemStatus: builder.mutation<any, { orderId: string; itemId: string; status: string }>({
      query: (body) => ({
        url: `/wrd/api/purchase-order/warehouse/${body.orderId}/item/${body.itemId}/status`,
        method: 'put',
        body: { status: body.status },
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS', 'INBOUND_PACKAGES'],
    }),

    // Update item details (dimensions, quantity)
    updateItemDetails: builder.mutation<any, { orderId: string; itemId: string; details: any }>({
      query: (body) => ({
        url: `/wrd/api/purchase-order/warehouse/${body.orderId}/item/${body.itemId}/details`,
        method: 'put',
        body: body.details,
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS'],
    }),

    // Set dimensions
    setDimensions: builder.mutation<
      any,
      {
        orderId: string;
        sku: string;
        height: number;
        width: number;
        length: number;
        weight: number;
        massUnit: 'G' | 'KG';
        lengthMetric: 'CM' | 'FT';
        quantityConfirmed: boolean;
      }
    >({
      query: ({ orderId, sku, ...body }) => ({
        url: `/wrd/api/purchase-order/warehouse/${orderId}/${sku}/set-dimensions`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS', 'PACKAGE_ITEM_DETAILS'],
    }),

    // Raise claim
    raiseClaim: builder.mutation<
      any,
      {
        orderId: string;
        sku: string;
        reason: string;
        notes?: string;
        attachmentsQuantity?: number;
      }
    >({
      query: ({ orderId, sku, ...body }) => ({
        url: `/wrd/api/purchase-order/warehouse/${orderId}/${sku}/raise-claim`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS', 'INBOUND_PACKAGES'],
    }),

    // Check full packages
    checkFullPackages: builder.mutation<
      any,
      {
        orderIds: string[];
        sku: string;
      }
    >({
      query: (body) => ({
        url: `/wrd/api/purchase-order/warehouse/full-packages`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['INBOUND_PACKAGES', 'INBOUND_PACKAGE_DETAILS'],
    }),

    // Get delivery note
    getDeliveryNote: builder.query<any, string>({
      query: (orderId) => ({
        url: `/wrd/api/order/warehouse/${orderId}/delivery-note`,
        method: 'get',
      }),
    }),

    // Generate label
    generateLabel: builder.mutation<
      any,
      {
        orderId: string;
        height: number;
        width: number;
        length: number;
        weight: number;
        massUnit: 'G' | 'KG';
        lengthMetric: 'CM' | 'FT';
      }
    >({
      query: ({ orderId, ...body }) => ({
        url: `/wrd/api/order/warehouse/generate-label/${orderId}`,
        method: 'post',
        body,
      }),
    }),

    // Check if task is done (polling)
    checkTaskStatus: builder.query<any, string>({
      query: (taskId) => ({
        url: `/wrd/api/task/${taskId}/is-done`,
        method: 'get',
      }),
    }),

    // Get generated label
    getGeneratedLabel: builder.query<any, string>({
      query: (orderId) => ({
        url: `/wrd/api/order/warehouse/${orderId}/label`,
        method: 'get',
      }),
    }),

    // Get item dimensions
    getItemDimensions: builder.query<any, string>({
      query: (itemId) => ({
        url: `/wrd/api/purchase-order-item/${itemId}/dimensions`,
        method: 'get',
      }),
      providesTags: ['PACKAGE_ITEM_DETAILS'],
    }),

    updateItemStep: builder.mutation<any, { itemId: string; step: string }>({
      query: ({ itemId, step }) => ({
        url: `/wrd/api/purchase-order-item/warehouse/${itemId}/step`,
        method: 'put',
        params: { step },
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS'],
    }),

    // Outbound order details
    outboundOrderDetails: builder.query<any, string>({
      query: (id) => ({
        url: `/wrd/api/order/warehouse/${id}/details`,
        method: 'get',
      }),
      providesTags: ['OUTBOUND_PACKAGE_DETAILS'],
    }),

    // Update purchase order status
    updatePurchaseOrderStatus: builder.mutation<any, { id: string; status: string }>({
      query: (body) => ({
        url: `/wrd/api/purchase-order/status`,
        method: 'put',
        body,
      }),
      invalidatesTags: ['INBOUND_PACKAGE_DETAILS', 'INBOUND_PACKAGES'],
    }),

    // Search item manually by SKU
    searchItem: builder.mutation<any, { orderId: string; text: string }>({
      query: (body) => ({
        url: `/wrd/api/purchase-order/warehouse/search-item`,
        method: 'post',
        body,
      }),
    }),
  }),
});

export const {
  useInboundPackageListQuery,
  useInboundPackageByIdQuery,
  useOutboundPackageListQuery,
  useOutboundPackageByIdQuery,
  useIdentifyOrderMutation,
  useIdentifyItemMutation,
  useUpdateItemStatusMutation,
  useUpdateItemDetailsMutation,
  useSetDimensionsMutation,
  useRaiseClaimMutation,
  useCheckFullPackagesMutation,
  useLazyGetDeliveryNoteQuery,
  useGenerateLabelMutation,
  useCheckTaskStatusQuery,
  useLazyGetGeneratedLabelQuery,
  useGetItemDimensionsQuery,
  useUpdateItemStepMutation,
  useOutboundOrderDetailsQuery,
  useUpdatePurchaseOrderStatusMutation,
  useSearchItemMutation,
} = warehousePackagesApi;
